import { Card, Button, Row, Col, Spinner } from 'reactstrap';

import {
  localDate,
  humanizeDurationnHoursMinutes,
  calculatePageOffset,
  seperateNumbersWithCommas,
} from '../../../utils/misc';
import { Table, Typography } from 'antd';
import '../gridStyle.css';
import { useState } from 'react';
import { toNumber } from 'lodash';
import { CSVLink } from 'react-csv';
import { useRef } from 'react';
import { map } from 'lodash';
import { useHostChargingSessions } from 'utils/queries/hostQueries';
import moment from 'moment';
import { useHostChargingSessionsExport } from 'utils/queries/hostQueries';
import ChargerSessionModal from 'components/Modals/ChargerSessionModal';

const { Text } = Typography;

export const ChargingSessionsGridHost = ({ startDate, endDate, cpid = '' }) => {
  const [customerSessionsPageNum, setCustomerSessionsPageNum] = useState(1);

  const csvLinkRef = useRef();
  const limit = 10;
  const [sortOrder, setSortOrder] = useState('ascend');
  const [orderBy, setOrderBy] = useState('START_DATE');

  const [selectedSession, setSelectedSession] = useState('');
  const [openChargerSessionModal, setOpenChargerSessionModal] = useState(false);
  const toggleChargerSessionModal = () =>
    setOpenChargerSessionModal(!openChargerSessionModal);

  const { data: sessionsResp, isLoading } = useHostChargingSessions({
    limit,
    offset: calculatePageOffset(limit, customerSessionsPageNum),
    startDate,
    endDate,
    orderBy,
    sortOrder: getSortOrder(),
    cpid,
  });
  const { data: sessions, count: totals } = sessionsResp || {};
  const {
    AMOUNT_CONSUMED,
    // AMOUNT_TAX,
    DURATION,
    KWH_CONSUMED,
    NUM: count,
  } = totals || {};

  const { data: exportSessionsResp, isLoading: isExporting } =
    useHostChargingSessionsExport({
      offset: 0,
      startDate,
      endDate,
      orderBy,
      sortOrder: getSortOrder(sortOrder),
      cpid,
    });
  const { data: exportedData } = exportSessionsResp || {};

  const columns = [
    {
      title: 'ID',
      dataIndex: 'TRANSACTIONID',
      visibility: true,
      render: (text, session) => (
        <span
          style={{ fontWeight: 600, color: '#317ffe' }}
          onClick={() => {
            setSelectedSession(session?.TRANSACTIONID);
            toggleChargerSessionModal();
          }}
        >
          {session?.TRANSACTIONID}
        </span>
      ),
      key: 'TRANSACTIONID',
    },
    {
      title: 'KWH Billed',
      dataIndex: 'KWH_BILLED',
      visibility: true,
      render: (text, session) => (
        <>{toNumber(session?.KWH_BILLED).toFixed(2)}kWh</>
      ),
      sorter: (a, b) => a?.KWH_BILLED - b?.KWH_BILLED,
      key: 'KWH_BILLED',
    },
    {
      title: 'STARTED AT',
      dataIndex: 'START_TIME',
      visibility: true,
      render: (text, session) => <>{localDate(session?.START_TIME)}</>,
      sorter: (a, b) => a?.START_TIME - b?.START_TIME,
      defaultSortOrder: 'ascend',
      key: 'START_TIME',
    },
    {
      title: 'ENDED AT',
      dataIndex: 'END_TIME',
      visibility: true,
      render: (text, session) => <>{localDate(session?.END_TIME)}</>,
      sorter: (a, b) => a?.END_TIME - b?.END_TIME,
      key: 'END_TIME',
    },
    {
      title: 'Duration',
      dataIndex: 'DURATION',
      visibility: true,
      render: (text, session) => (
        <>
          {
            humanizeDurationnHoursMinutes(
              moment
                .duration(
                  new Date(session?.END_TIME) - new Date(session?.START_TIME),
                )
                .asMinutes(),
            ).string
          }
        </>
      ),

      sorter: (a, b) =>
        moment
          .duration(new Date(a?.END_TIME) - new Date(a?.START_TIME))
          .asMinutes() -
        moment
          .duration(new Date(b?.END_TIME) - new Date(b?.START_TIME))
          .asMinutes(),
      key: 'DURATION',
    },
    {
      title: 'Amount',
      dataIndex: 'AMOUNT_BILLED',
      visibility: true,
      render: (text, session) => (
        <>
          ₹
          {seperateNumbersWithCommas(
            (toNumber(session?.AMOUNT_BILLED) / 100).toFixed(2),
          )}
        </>
      ),
      sorter: (a, b) => a?.AMOUNT_BILLED - b?.AMOUNT_BILLED,
      key: 'AMOUNT_BILLED',
    },
    {
      title: 'Meter Start',
      dataIndex: 'METER_START',
      visibility: true,
      render: (text, session) => <>{session?.METER_START}wH</>,
      key: 'METER_START',
    },
    {
      title: 'Meter Stop',
      dataIndex: 'METER_STOP',
      visibility: true,
      render: (text, session) => <>{session?.METER_STOP}wH</>,
      key: 'METER_STOP',
    },
    {
      title: 'Charger Id',
      dataIndex: 'CPID',
      visibility: true,
      render: (text, session) => <>{session?.CPID}</>,
      key: 'CPID',
    },
  ];

  function getSortOrder() {
    let order = '';
    if (sortOrder === 'ascend') {
      order = 'ASC';
    } else if (sortOrder === 'descend') {
      order = 'DESC';
    }
    return order;
  }

  function generateChargingSessionsCsvData(mySessions) {
    if (!mySessions || mySessions?.length === 0) return [];
    let formattedSessions = map(mySessions, (session) => {
      return {
        'TRANSACTION ID': session?.TRANSACTIONID,
        'KWH BILLED': `${toNumber(session?.KWH_BILLED).toFixed(2)}kWh`,
        'STARTED AT': localDate(session?.START_TIME),
        'ENDED AT': localDate(session?.END_TIME),
        DURATION: humanizeDurationnHoursMinutes(
          moment
            .duration(
              new Date(session?.END_TIME) - new Date(session?.START_TIME),
            )
            .asMinutes(),
        ).string,
        AMOUNT: `₹
          ${seperateNumbersWithCommas(
            (toNumber(session?.AMOUNT_BILLED) / 100).toFixed(2),
          )}`,
        'Meter Start': `${session?.METER_START}wH`,
        'Meter Stop': `${session?.METER_STOP}wH`,
        'Charger Id': session?.CPID,
      };
    });

    const rowOfTotals = {
      'TRANSACTION ID': 'Total',
      'KWH BILLED': `${toNumber(KWH_CONSUMED).toFixed(2)}kWh`,
      'STARTED AT': '',
      'ENDED AT': '',
      DURATION: DURATION ? `${DURATION} m` : 0,
      AMOUNT: `₹${seperateNumbersWithCommas(
        (toNumber(AMOUNT_CONSUMED) / 100).toFixed(2),
      )}`,
      'Meter Start': '',
      'Meter Stop': '',
      'Charger Id': '',
    };
    formattedSessions.push(rowOfTotals);
    return formattedSessions;
  }

  const exportToCSV = () => {
    csvLinkRef?.current?.link.click();
  };

  const getTitle = () => {
    return (
      <div className="d-flex justify-content-space-between align-items-center">
        {sessions && (
          <>
            <h3 className="mb-0">
              Charging Sessions{' '}
              <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                ({startDate} - {endDate})
              </span>
            </h3>

            {(isLoading || isExporting) && (
              <Spinner style={{ color: '#5e72e4' }} />
            )}

            <div className="d-flex justify-content-flex-end  align-items-center">
              <div style={{ marginRight: 10, fontWeight: 400 }}>
                (Total Sessions: {count})
              </div>
              <Button className="jp-csv-button" onClick={exportToCSV}>
                {' '}
                Export as CSV
              </Button>
              <CSVLink
                ref={csvLinkRef}
                data={generateChargingSessionsCsvData(exportedData)}
                filename="ChargingSessions.csv"
                separator=","
                className="csv-button"
                style={{
                  display: 'none',
                }}
              >
                Export as CSV
              </CSVLink>
            </div>
          </>
        )}
      </div>
    );
  };

  const getSummary = () => {
    if (!(sessions || sessions?.length > 0)) return null;
    return (
      <Table.Summary fixed>
        <Table.Summary.Row fixed style={{ fontWeight: '600' }}>
          <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            <Text>
              {KWH_CONSUMED ? `${toNumber(KWH_CONSUMED).toFixed(2)} KWh` : 0}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} />
          <Table.Summary.Cell index={3} />
          <Table.Summary.Cell index={4}>
            <Text>{DURATION ? `${DURATION} m` : 0}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4}>
            <Text>
              <Text>
                {AMOUNT_CONSUMED
                  ? `₹${seperateNumbersWithCommas(
                      (toNumber(AMOUNT_CONSUMED) / 100).toFixed(2),
                    )}`
                  : 0}
              </Text>
            </Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div>
      <Row style={{ marginTop: '40px' }}>
        <Col className="order-xl-1" xl="12">
          <Card className="shadow">
            {
              <Table
                rowKey="key"
                className="cs-grid table-striped-rows shadow"
                tableLayout="fixed"
                sscroll={{ y: 350, x: 'max-content' }}
                columns={columns}
                dataSource={sessions || []}
                scroll={{ y: '50vh' }}
                style={{ border: '1px solid #e7e7e9' }}
                title={() => getTitle()}
                pagination={{
                  current: customerSessionsPageNum,
                  defaultCurrent: 1,
                  pageSize: limit,
                  total: count,
                  hideOnSinglePage: limit >= count,
                  showSizeChanger: false,
                }}
                summary={(tableData) => getSummary()}
                onChange={(pagination, filters, sorter) => {
                  setSortOrder(sorter.order);
                  setOrderBy(sorter.columnKey);
                  setCustomerSessionsPageNum(pagination.current);
                }}
              />
            }
          </Card>
        </Col>
      </Row>

      <ChargerSessionModal
        modalOpen={openChargerSessionModal}
        toggleModalOpen={toggleChargerSessionModal}
        transactionId={selectedSession}
      />
    </div>
  );
};
